import { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const FIELD_LABEL_OVERRIDES = { qty: "QTY" };
const fieldLabel = (name) => FIELD_LABEL_OVERRIDES[name] || name;

export function formatFieldDisplayValue(f, value) {
  if (value === undefined || value === null || value === "") return "-";
  if (f.name === "CBM") {
    const num = Number(value);
    return Number.isNaN(num) ? "-" : num.toFixed(2);
  }
  if (f.name === "qty") {
    const num = Number(value);
    return Number.isNaN(num) ? "-" : String(Math.round(num));
  }
  return String(value).replace(/\r/g, " ").trim() || "-";
}

export function AutoGrowTextarea({ value, onChange, ...props }) {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(resize, [value]);
  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={2}
      className="min-h-[60px] resize-none overflow-hidden rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
      {...props}
    />
  );
}

function EditableField({ f, formData, editMode, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-zinc-500">{fieldLabel(f.name)}</Label>
      {editMode ? (
        <Input
          value={formData[f.name] ?? ""}
          onChange={(e) => onChange(f.name, e.target.value)}
          type={f.result === "number" ? "number" : "text"}
          step={f.result === "number" ? "any" : undefined}
          className={`rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600 ${
            f.result === "number" ? "font-mono-data text-right" : ""
          }`}
          data-testid={`product-field-input-${f.name}`}
        />
      ) : (
        <div
          className={`rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-sm text-zinc-800 ${
            f.result === "number" ? "font-mono-data text-right" : ""
          }`}
          data-testid={`product-field-value-${f.name}`}
        >
          {formatFieldDisplayValue(f, formData[f.name])}
        </div>
      )}
    </div>
  );
}

function ReadonlyField({ f, formData }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-xs text-zinc-400">{fieldLabel(f.name)}</Label>
      <div
        className={`rounded-sm border border-zinc-200 bg-zinc-100 px-2 py-1.5 text-sm text-zinc-500 ${
          f.result === "number" ? "font-mono-data text-right" : ""
        }`}
        data-testid={`product-readonly-field-${f.name}`}
      >
        {formatFieldDisplayValue(f, formData[f.name])}
      </div>
    </div>
  );
}

export function DrawerDetailsSection({ readOnly, formData, editMode, editableFields, readonlyFields, onChange }) {
  if (readOnly) {
    return (
      <div>
        <p className="mb-2 text-xs uppercase tracking-[0.05em] text-zinc-500">Details</p>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-zinc-500">Item Number</Label>
            <div
              className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-sm font-semibold text-zinc-900"
              data-testid="product-mobile-item-no"
            >
              {String(formData["item no"] ?? "-").replace(/\r/g, " ") || "-"}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-zinc-500">Description</Label>
            <div
              className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-sm text-zinc-800"
              data-testid="product-mobile-description"
            >
              {String(formData["description"] ?? "-").replace(/\r/g, " ") || "-"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cbmField = editableFields.find((f) => f.name === "CBM");
  const dateField = editableFields.find((f) => f.name === "date");
  const qtyField = readonlyFields.find((f) => f.name === "qty");
  const inlineNames = ["CBM", "date"];
  const otherEditable = editableFields.filter((f) => !inlineNames.includes(f.name));
  const otherReadonly = readonlyFields.filter((f) => f.name !== "qty");

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs uppercase tracking-[0.05em] text-zinc-500">Details</p>

      <div className="grid grid-cols-3 gap-3" data-testid="product-cbm-qty-date-row">
        {cbmField && <EditableField f={cbmField} formData={formData} editMode={editMode} onChange={onChange} />}
        {qtyField && <ReadonlyField f={qtyField} formData={formData} />}
        {dateField && <EditableField f={dateField} formData={formData} editMode={editMode} onChange={onChange} />}
      </div>

      {(otherEditable.length > 0 || otherReadonly.length > 0) && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {otherEditable.map((f) => (
            <EditableField key={f.name} f={f} formData={formData} editMode={editMode} onChange={onChange} />
          ))}
          {otherReadonly.map((f) => (
            <ReadonlyField key={f.name} f={f} formData={formData} />
          ))}
        </div>
      )}
    </div>
  );
}
